import { useEffect, useState } from "react";
import { OfficerLayout } from "@/components/OfficerLayout";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { StatutoryRegister, formTabLabel } from "@/components/StatutoryRegister";
import { Users2, Gavel, ScrollText } from "lucide-react";

export default function GovernancePage() {
  const { lang } = useLang();
  const { user } = useAuth();
  const [tab, setTab] = useState("meetings");
  return (
    <OfficerLayout>
      <div>
        <div className="mb-5">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E87722]">Direct Democracy</div>
          <h1 className="mt-2 font-heading text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Users2 className="w-7 h-7 text-[#0A3161]" />
            {lang === "hi" ? "ग्राम सभा एवं संकल्प" : "Gram Sabha & Resolutions"}
          </h1>
        </div>
        <div className="flex border-b border-slate-200 mb-4 overflow-x-auto">
          {[
            { k: "meetings", label: lang === "hi" ? "बैठकें" : "Meetings", icon: Users2 },
            { k: "resolutions", label: lang === "hi" ? "संकल्प रजिस्टर" : "Resolution Register", icon: Gavel },
            { k: "II", label: formTabLabel("II", lang), icon: ScrollText },
            { k: "V", label: formTabLabel("V", lang), icon: ScrollText },
            { k: "III", label: formTabLabel("III", lang), icon: ScrollText },
          ].map((x) => {
            const I = x.icon;
            return (
              <button key={x.k} onClick={() => setTab(x.k)} data-testid={`gov-tab-${x.k}`}
                className={`px-5 py-3 text-sm font-semibold border-b-2 inline-flex items-center gap-1.5 ${tab === x.k ? "border-[#E87722] text-[#0A3161]" : "border-transparent text-slate-500"}`}>
                <I className="w-4 h-4" /> {x.label}
              </button>
            );
          })}
        </div>
        {tab === "meetings" && <Meetings user={user} />}
        {tab === "resolutions" && <Resolutions user={user} />}
        {["II", "V", "III"].includes(tab) && <StatutoryRegister code={tab} />}
      </div>
    </OfficerLayout>
  );
}

function Meetings({ user }) {
  const [rows, setRows] = useState([]);
  const [picked, setPicked] = useState(null);
  useEffect(() => {
    const params = user?.panchayat_id ? { panchayat_id: user.panchayat_id } : {};
    api.get("/meetings", { params }).then((r) => setRows(r.data));
  }, [user]);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 rounded-md border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="meetings-table">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            <tr><th className="text-left px-4 py-3">Meeting No.</th><th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Date</th><th className="text-left px-4 py-3">Venue</th>
                <th className="text-right px-4 py-3">Attendance</th></tr>
          </thead>
          <tbody>
            {rows.map((m) => (
              <tr key={m.id} onClick={() => setPicked(m)} className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 ${picked?.id === m.id ? "bg-[#0A3161]/5" : ""}`}>
                <td className="px-4 py-3 font-mono text-xs">{m.meeting_no}</td>
                <td className="px-4 py-3 capitalize">{m.meeting_type?.replace("_", " ")}</td>
                <td className="px-4 py-3 text-xs">{m.meeting_date}</td>
                <td className="px-4 py-3 text-xs">{m.venue}</td>
                <td className="px-4 py-3 text-right font-mono">{m.attendance_count}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-slate-400">No meetings</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="rounded-md border border-slate-200 bg-white p-5">
        {picked ? (
          <div data-testid="meeting-detail">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#E87722] font-semibold">{picked.meeting_type?.replace("_", " ")}</div>
            <div className="font-mono text-xs text-slate-500 mt-1">{picked.meeting_no}</div>
            <div className="mt-3 text-sm font-semibold">Chair: {picked.chaired_by}</div>
            <div className="mt-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Agenda</div>
            <ul className="mt-1 list-disc list-inside text-sm">{(picked.agenda || []).map((a, i) => <li key={i}>{a}</li>)}</ul>
            {picked.minutes && (
              <>
                <div className="mt-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Minutes</div>
                <div className="mt-1 text-sm whitespace-pre-wrap">{picked.minutes}</div>
              </>
            )}
          </div>
        ) : (
          <div className="text-sm text-slate-500 py-10 text-center">Select a meeting</div>
        )}
      </div>
    </div>
  );
}

function Resolutions({ user }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    const params = user?.panchayat_id ? { panchayat_id: user.panchayat_id } : {};
    api.get("/resolutions", { params }).then((r) => setRows(r.data));
  }, [user]);
  return (
    <div className="rounded-md border border-slate-200 bg-white overflow-x-auto">
      <table className="w-full text-sm" data-testid="resolutions-table">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
          <tr><th className="text-left px-4 py-3">Resolution No.</th><th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Subject</th><th className="text-left px-4 py-3">Decision</th>
              <th className="text-left px-4 py-3">Proposed / Seconded</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-slate-100">
              <td className="px-4 py-3 font-mono text-xs">{r.resolution_no}</td>
              <td className="px-4 py-3 text-xs">{r.meeting_date}</td>
              <td className="px-4 py-3 font-medium">{r.subject}</td>
              <td className="px-4 py-3 text-xs max-w-md">{r.decision}</td>
              <td className="px-4 py-3 text-xs">{r.proposed_by}<br /><span className="text-slate-500">{r.seconded_by}</span></td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-slate-400">No resolutions</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
